import React, { useState } from 'react';
import { Box, Avatar, Typography, IconButton, Tooltip } from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Undo as UndoIcon,
  Forward as ForwardIcon,
  ErrorOutline as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import type { Message } from '../types/message';
import { avatarText, anonTag } from '../utils/format';

interface Props {
  message: Message;
  meId: number;
  group: boolean;
  onRecall?: (m: Message) => void;
  onDelete?: (m: Message) => void;
  onForward?: (m: Message) => void;
  onRetry?: (m: Message) => void;
}

export default function ChatMessage({ message: m, meId, group, onRecall, onDelete, onForward, onRetry }: Props) {
  const [hover, setHover] = useState(false);
  const mine = m.sender_id === meId;

  // 系统消息 → 居中灰字
  if (m.type === 'system') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.05)',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            textAlign: 'center',
            maxWidth: '80%',
          }}
        >
          {m.content}
        </Typography>
      </Box>
    );
  }

  // 已撤回/已删除 → 居中提示(区分双方)
  if (m.deleted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', px: 1 }}>
          {mine ? '你撤回了一条消息' : `${m.sender?.nickname || m.sender?.username || '对方'}撤回了一条消息`}
        </Typography>
      </Box>
    );
  }

  const failed = m.status === 'failed';

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        display: 'flex',
        flexDirection: mine ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        mb: 1.5,
        px: 2,
      }}
    >
      {!mine && (
        <Avatar src={m.sender?.avatar || undefined} sx={{ width: 36, height: 36, mr: 1, bgcolor: 'primary.light', fontSize: 15 }}>
          {avatarText(m.sender)}
        </Avatar>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', maxWidth: '62%' }}>
        {/* 发送者昵称(群聊,对方)+ 匿名标签(泄露溯源) */}
        {!mine && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.3, px: 0.5 }}>
            {group ? (m.sender?.nickname || m.sender?.username) : ''}
            <Box component="span" sx={{ ml: 0.5, color: 'text.disabled', fontSize: 11 }} title="匿名ID(截图泄露时可据此溯源)">
              匿名 {anonTag(m.sender_id)}
            </Box>
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: mine ? 'row-reverse' : 'row' }}>
          {/* 操作按钮(悬停显示) */}
          {hover && (onForward || onDelete || onRecall) && (
            <Box sx={{ display: 'flex', mr: mine ? 1 : 0, ml: mine ? 0 : 1 }}>
              {onForward && (
                <Tooltip title="转发">
                  <IconButton size="small" onClick={() => onForward(m)}>
                    <ForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="删除">
                  <IconButton size="small" onClick={() => onDelete(m)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onRecall && mine && (
                <Tooltip title="撤回">
                  <IconButton size="small" onClick={() => onRecall(m)}>
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          {/* 状态指示 */}
          {mine &&
            (m.status === 'sending' ? (
              <Tooltip title="发送中">
                <ScheduleIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5 }} />
              </Tooltip>
            ) : failed ? (
              <Tooltip title={m.sendError || '发送失败'}>
                <ErrorIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5, cursor: 'pointer' }} onClick={() => onRetry?.(m)} />
              </Tooltip>
            ) : null)}
          {/* 气泡 */}
          <Box
            onClick={failed ? () => onRetry?.(m) : undefined}
            sx={{
              bgcolor: mine ? 'primary.main' : '#fff',
              color: mine ? '#fff' : 'text.primary',
              px: 1.4,
              py: 0.8,
              borderRadius: mine ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              cursor: failed ? 'pointer' : 'default',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {m.content}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
